/* eslint-disable react-native/no-inline-styles */

import React, { useLayoutEffect, useRef, useState, useMemo } from 'react';
import { LayoutChangeEvent, View, useWindowDimensions } from 'react-native';
import {
  DocketItemStatus,
  DocketStatus,
  LayoutStrategy,
  NotifierDocketItems,
} from '@oolio-group/domain';
import { useScale } from '../../../hooks/useScale';
import { useSession } from '../../../hooks/useSession';
import { useDockets } from '../../../hooks/useDockets';
import { useOrderTypes } from '../../../hooks/useOrderTypes';
import { useSalesChannels } from '../../../hooks/useSalesChannels';
import DocketItem from '../../../component/DocketItem/DocketItem';
import { PaginatedDocketItems } from '../../../utils/layoutHelper';
import MainScreenStyles from '../MainScreen.styles';
import { DOCKET_WIDTH, stickyHeaders } from '../../../common/constants';
import {
  DocketDisplayItem,
  DocketDetailsProps,
  DocketDisplayOrderItem,
  DocketDisplayItemType,
} from '../../../common/types';
import { bulkSelectVar, selectedDocketVar } from '../../../state/apolloVars';
import ScrollView from '../../../component/ScrollView/ScrollView';

export interface DocketsGridProps {
  displayDocketItems: PaginatedDocketItems[];
  status: DocketStatus;
  currentPage: number;
  enablePrint?: boolean;
  setColumnsHeight?: (height: number) => void;
  columnsHeight?: number;
  onToggleDocketDetails: (a: DocketDetailsProps) => void;
  selectedDocket?: string;
  closeDocketDetails?: () => void;
  rowsPerPage?: number;
}

const DocketsGrid: React.FC<DocketsGridProps> = ({
  displayDocketItems,
  status,
  currentPage,
  enablePrint,
  setColumnsHeight,
  columnsHeight,
  onToggleDocketDetails,
  selectedDocket,
  closeDocketDetails,
  rowsPerPage = 2, // Default to 2 for backward compatibility
}) => {
  const {
    bumpDocketWithStatus,
    recallDocket,
    bumpDocketItem,
    recallDocketItem,
    serveDocketItem,
    reBumpDocketItem,
    printKitchenDocket,
  } = useDockets();
  const { scale } = useScale();
  const { session } = useSession();
  const styles = MainScreenStyles();
  const { orderTypes } = useOrderTypes();
  const { width } = useWindowDimensions();
  const { salesChannels } = useSalesChannels();

  const [itemWidth, setItemWidth] = useState<number>(0);
  const heightRef = useRef<any>(undefined);
  const isBulkMode = bulkSelectVar();
  const selectedDocketId = Object.keys(selectedDocketVar())?.[0];
  const isSingleSelectedMode = !isBulkMode && selectedDocketId;

  useLayoutEffect(() => {
    const columnsPossible = Math.min(
      Math.floor(width / scale(DOCKET_WIDTH)),
      session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen ?? 8,
    );
    setItemWidth(Math.floor(width / columnsPossible - scale(12)));
  }, [scale, width, session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen]);

  const handleOnPressDocketHeader = async (
    docketId: string,
    notifierItems: NotifierDocketItems[] | undefined,
  ) => {
    let result;

    if (docketId === selectedDocket && closeDocketDetails) {
      closeDocketDetails();
    }
    if (status == DocketStatus.CREATED) {
      result = bumpDocketWithStatus(
        docketId,
        session?.rebumpToggle
          ? DocketItemStatus.REBUMPED
          : DocketItemStatus.BUMPED,
        notifierItems,
      );
    } else {
      result = recallDocket(docketId);
    }

    return result;
  };

  const handleOnPressDocketItem = async (
    docketItemId: string,
    itemStatus: DocketItemStatus,
  ) => {
    if (
      itemStatus == DocketItemStatus.CREATED ||
      itemStatus == DocketItemStatus.RECALLED
    ) {
      await bumpDocketItem(docketItemId);
    } else if (
      status == DocketStatus.CREATED &&
      itemStatus == DocketItemStatus.BUMPED
    ) {
      session?.rebumpToggle
        ? await reBumpDocketItem(docketItemId)
        : await serveDocketItem(docketItemId);
    } else if (
      status == DocketStatus.CREATED &&
      itemStatus == DocketItemStatus.REBUMPED
    ) {
      await serveDocketItem(docketItemId);
    } else {
      await recallDocketItem(docketItemId);
    }
  };

  const onLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (!columnsHeight || columnsHeight <= 0) {
      setColumnsHeight?.(height);
    }
  };

  const getRowItems = (
    rows: { row: number; items: DocketDisplayItem[] }[],
    targetRow: number,
  ): DocketDisplayItem[] => {
    const rowData = rows.find(r => r.row === targetRow);
    return rowData ? rowData.items : [];
  };

  // Calculate fixed row height for grid layout
  const fixedRowHeight = useMemo(() => {
    if (!columnsHeight) return 0; // Default fallback height
    return Math.floor(columnsHeight / rowsPerPage) - scale(10);
  }, [columnsHeight, rowsPerPage, scale]);

  // Check if we're using grid layout
  const isGridLayout = useMemo(() => {
    return (
      session?.kitchenDisplay?.layoutStrategy?.strategy === LayoutStrategy.GRID
    );
  }, [session?.kitchenDisplay?.layoutStrategy?.strategy]);

  return (
    <View
      style={[styles.grid, isGridLayout && { columnGap: scale(10) }].filter(
        Boolean,
      )}
      ref={heightRef}
      onLayout={onLayout}
    >
      {isSingleSelectedMode && <View style={styles.overlay} />}
      {displayDocketItems[currentPage]?.columns.map((col, columnIndex) => {
        return (
          <View key={columnIndex} style={styles.gridColumn}>
            {Array.from({ length: rowsPerPage }, (_, rowIndex) => {
              const rowNumber = rowIndex + 1;
              const rowItems = getRowItems(col.rows, rowNumber);

              const headerItems = rowItems.filter(item =>
                stickyHeaders.includes(item?.type),
              );

              // Calculate total height of all headers for scroll container
              const totalHeadersHeight =
                headerItems.length > 0
                  ? headerItems.reduce((total, item) => {
                      return (
                        total +
                        scale(item.height) +
                        (item.type === DocketDisplayItemType.CONTINUED
                          ? // Add extra space for continued items to account for margin
                            scale(4)
                          : 0)
                      );
                    }, 0)
                  : 0;

              const scrollContainerHeight = Math.max(
                0,
                fixedRowHeight - totalHeadersHeight,
              );
              return isGridLayout ? (
                <View
                  key={`row-${rowNumber}`}
                  style={{
                    height: fixedRowHeight,
                    maxHeight: fixedRowHeight,
                    width: itemWidth,
                    paddingHorizontal: scale(4),
                  }}
                >
                  {isSingleSelectedMode &&
                    rowItems.some(item => {
                      const docketDisplayItem = item as DocketDisplayOrderItem;
                      const docketId =
                        docketDisplayItem?.docketId || docketDisplayItem?.id;
                      return selectedDocketId !== docketId;
                    }) && <View style={styles.overlay} />}
                  <View>
                    {headerItems.map((headerItem, headerIndex) => (
                      <DocketItem
                        key={`header-${rowNumber}-${headerIndex}`}
                        item={headerItem}
                        status={status}
                        orderTypes={orderTypes}
                        salesChannels={salesChannels}
                        onPressDocketHeader={handleOnPressDocketHeader}
                        onPressDocketItem={handleOnPressDocketItem}
                        onToggleDocketDetails={onToggleDocketDetails}
                        enablePrint={enablePrint}
                        printKitchenDocket={printKitchenDocket}
                      />
                    ))}
                  </View>
                  <ScrollView
                    style={[
                      styles.rowContainer,
                      {
                        height: scrollContainerHeight,
                        maxHeight: scrollContainerHeight,
                      },
                    ]}
                  >
                    {rowItems.map((item, itemIndex) => {
                      const docketDisplayItem = item as DocketDisplayOrderItem;
                      const docketId =
                        docketDisplayItem?.docketId || docketDisplayItem?.id;
                      if (stickyHeaders.includes(item?.type)) {
                        return null;
                      }
                      return (
                        <View
                          key={`row-${rowNumber}-item-${itemIndex}`}
                          style={[
                            styles.gridItem,
                            {
                              width: itemWidth,
                              paddingRight: scale(12),
                            },
                          ]}
                        >
                          {isSingleSelectedMode &&
                            selectedDocketId !== docketId && (
                              <View style={styles.overlay} />
                            )}
                          <DocketItem
                            key={`${item.name}-${rowNumber}-${itemIndex}`}
                            item={item}
                            status={status}
                            orderTypes={orderTypes}
                            salesChannels={salesChannels}
                            onPressDocketHeader={handleOnPressDocketHeader}
                            onPressDocketItem={handleOnPressDocketItem}
                            onToggleDocketDetails={onToggleDocketDetails}
                            enablePrint={enablePrint}
                            printKitchenDocket={printKitchenDocket}
                          />
                        </View>
                      );
                    })}
                  </ScrollView>
                </View>
              ) : (
                <View
                  key={`row-${rowNumber}`}
                  style={[
                    styles.rowContainer,
                    {
                      height: fixedRowHeight,
                      maxHeight: fixedRowHeight,
                    },
                  ]}
                >
                  {rowItems.map((item, itemIndex) => {
                    const docketDisplayItem = item as DocketDisplayOrderItem;
                    const docketId =
                      docketDisplayItem?.docketId || docketDisplayItem?.id;
                    return (
                      <View
                        key={`row-${rowNumber}-item-${itemIndex}`}
                        style={[
                          styles.item,
                          {
                            width: itemWidth,
                          },
                        ]}
                      >
                        {isSingleSelectedMode &&
                          selectedDocketId !== docketId && (
                            <View style={styles.overlay} />
                          )}
                        <DocketItem
                          key={`${item.name}-${rowNumber}-${itemIndex}`}
                          item={item}
                          status={status}
                          orderTypes={orderTypes}
                          salesChannels={salesChannels}
                          onPressDocketHeader={handleOnPressDocketHeader}
                          onPressDocketItem={handleOnPressDocketItem}
                          onToggleDocketDetails={onToggleDocketDetails}
                          enablePrint={enablePrint}
                          printKitchenDocket={printKitchenDocket}
                        />
                      </View>
                    );
                  })}
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );
};

export default DocketsGrid;
